
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OutfitLayer, WardrobeItem, AccessorySet, UserProfile, AppPage } from '../types';
import { Trash2Icon, UndoIcon, RepeatIcon, PaletteIcon, SearchIcon, PlusIcon, MessageSquareIcon, SaveIcon, FileTextIcon, SlidersHorizontalIcon, SparklesIcon, UserFocusIcon, SunIcon, GlassesIcon, HatIcon, WatchIcon, ShoppingBagIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { hexToHsl } from '../lib/utils';
import Spinner from './Spinner';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import PremiumFeatureModal from './PremiumFeatureModal';

interface OutfitStackProps {
  currentLayer: OutfitLayer;
  onRemoveGarment: (garmentId: string) => void;
  onRemoveAccessory: (accessoryId: string) => void;
  onReorderAccessory: (draggedId: string, targetId: string, isAfter: boolean) => void;
  onUndo: () => void;
  canUndo: boolean;
  onRedo: () => void;
  canRedo: boolean;
  onSetColorPickerItem: (item: WardrobeItem) => void;
  onFindSimilar: () => void; // Changed to generic handler
  onApplyAccessorySet: (set: AccessorySet) => void;
  predefinedAccessorySets: AccessorySet[];
  onGetStyleTips: () => void;
  onAnalyzeOutfit: () => void;
  onAnalyzeColor: () => void; // New prop for Color Analysis
  onSuggestVariations: () => void;
  onSaveOutfit: () => void;
  onNavigate: (page: AppPage) => void;
  userProfile: UserProfile;
  isLoading: boolean;
  isModelLoaded: boolean;
  onAddToLookbook: () => void;
  loadedOutfitId: string | null;
  isGuest: boolean;
  onAdjustItem?: (item: WardrobeItem) => void;
  onApplyCreativeTool?: (toolId: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => void;
  onGenerateAttitude?: () => void;
}

const OutfitStack: React.FC<OutfitStackProps> = ({
  currentLayer,
  onRemoveGarment,
  onRemoveAccessory,
  onReorderAccessory,
  onUndo,
  canUndo,
  onRedo,
  canRedo,
  onSetColorPickerItem,
  onFindSimilar,
  onApplyAccessorySet,
  predefinedAccessorySets,
  onGetStyleTips,
  onAnalyzeOutfit,
  onAnalyzeColor,
  onSuggestVariations,
  onSaveOutfit,
  onNavigate,
  userProfile,
  isLoading,
  isModelLoaded,
  onAddToLookbook,
  loadedOutfitId,
  isGuest,
  onAdjustItem,
  onApplyCreativeTool,
  onGenerateAttitude,
}) => {
  const { t } = useTranslation();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [isAfter, setIsAfter] = useState(true);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [requiredLevelForModal, setRequiredLevelForModal] = useState('Premium');

  const hasPremiumAccess = userProfile.membershipLevel &&
    ['premium', 'advanced', 'nextgen'].includes(userProfile.membershipLevel.toLowerCase()) &&
    !(userProfile.membershipExpiry && new Date(userProfile.membershipExpiry + 'T23:59:59') < new Date());

  const getLookbookTooltip = () => {
    if (isGuest) {
      return t('nav_guestCta');
    }
    const level = (userProfile.membershipLevel || 'free').toLowerCase();
    if (level === 'free' || level === 'basic') {
      return t('feature_locked_premium_generic');
    }
    if (!currentLayer.garments || currentLayer.garments.length === 0) {
      return t('alert_selectGarmentFirst');
    }
    if (!loadedOutfitId) {
      return t('outfitStack_saveOutfitFirstForLookbook');
    }
    return t('outfitStack_addToLookbook');
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    if (targetId !== draggedId) {
      setDropTargetId(targetId);
      const rect = e.currentTarget.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      setIsAfter(e.clientY > midpoint);
    }
  };

  const handleDragLeave = () => {
    setDropTargetId(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    if (draggedId && draggedId !== targetId) {
      onReorderAccessory(draggedId, targetId, isAfter);
    }
    setDraggedId(null);
    setDropTargetId(null);
  };

  const renderItem = (item: WardrobeItem, isAccessory: boolean) => {
    const isDragging = draggedId === item.id;
    const isDropTarget = dropTargetId === item.id;

    const color = isAccessory
      ? currentLayer.appliedAccessoryColors[item.id]
      : currentLayer.appliedMainGarmentColor;

    const { l } = hexToHsl(color || item.defaultColor || '#ccc');
    const iconColor = l > 50 ? '#000000' : '#FFFFFF';

    return (
      <motion.div
        key={item.id}
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        draggable={isAccessory}
        onDragStart={isAccessory ? (e => handleDragStart(e as unknown as React.DragEvent<HTMLDivElement>, item.id)) : undefined}
        onDragOver={isAccessory ? (e => handleDragOver(e as unknown as React.DragEvent<HTMLDivElement>, item.id)) : undefined}
        onDragLeave={isAccessory ? handleDragLeave : undefined}
        onDrop={isAccessory ? (e => handleDrop(e as unknown as React.DragEvent<HTMLDivElement>, item.id)) : undefined}
        className={`relative flex items-center justify-between p-2 rounded-lg border border-gray-200/80 dark:border-gray-600/80 shadow-sm transition-all duration-200
          ${isDragging ? 'opacity-50 scale-105 shadow-xl' : ''}
          ${isDropTarget ? 'bg-gray-200 dark:bg-gray-600' : 'bg-white/50 dark:bg-gray-700/50'}
        `}
      >
        <AnimatePresence>
          {isDropTarget && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: '3px' }}
              exit={{ height: 0 }}
              className={`absolute left-1 right-1 bg-indigo-500 rounded-full ${isAfter ? 'bottom-[-2px]' : 'top-[-2px]'}`}
            />
          )}
        </AnimatePresence>
        <div className="flex items-center overflow-hidden flex-1">
          {(() => {
            const styleRef = useDynamicStyle<HTMLButtonElement>({
              '--bg-color': color || item.defaultColor || '#ccc'
            });
            const iconStyleRef = useDynamicStyle<SVGSVGElement>({
              '--icon-color': iconColor
            });

            return (
              <button
                ref={styleRef}
                onClick={() => onSetColorPickerItem(item)}
                title={t('outfitStack_changeColor_aria', { itemName: t(item.nameKey) })}
                className="color-picker-trigger relative group flex-shrink-0 w-8 h-8 rounded-full border-2 border-white dark:border-gray-500 shadow-sm mr-3 flex items-center justify-center bg-[var(--bg-color)]"
              >
                <PaletteIcon
                  ref={iconStyleRef}
                  className="w-4 h-4 text-white opacity-0 group-hover:opacity-75 transition-opacity text-[var(--icon-color)]"
                />
              </button>
            );
          })()}
          <span className="font-semibold text-gray-800 dark:text-gray-100 truncate" title={t(item.nameKey)}>
            {t(item.nameKey)}
          </span>
        </div>
        <div className="flex items-center flex-shrink-0 gap-1">
          {onAdjustItem && (
            <button
              onClick={() => onAdjustItem(item)}
              className="p-2 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors hover:scale-110"
              aria-label={t('outfitStack_adjust_aria', { itemName: t(item.nameKey) })}
              title={t('outfitStack_adjust_aria', { itemName: t(item.nameKey) })}
            >
              <SlidersHorizontalIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={isAccessory ? () => onRemoveAccessory(item.id) : () => onRemoveGarment(item.id)}
            className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-500 transition-colors hover:scale-110"
            aria-label={t('outfitStack_remove_aria', { itemName: t(item.nameKey) })}
          >
            <Trash2Icon className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div id="onboarding-outfit-stack" className="h-full flex flex-col">
      <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
        <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-white">
          {t('outfitStack_title')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            id="action-undo"
            onClick={onUndo}
            disabled={!canUndo}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title={t('outfitStack_undo')}
            aria-label={t('outfitStack_undo')}
          >
            <UndoIcon className="w-5 h-5" />
          </button>
          <button
            id="action-redo"
            onClick={onRedo}
            disabled={!canRedo}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title={t('outfitStack_redo')}
            aria-label={t('outfitStack_redo')}
          >
            <RepeatIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="space-y-2 flex-grow">
        {currentLayer.garments && currentLayer.garments.length > 0 ? (
          <AnimatePresence>
            {currentLayer.garments.map(g => renderItem(g, false))}
          </AnimatePresence>
        ) : (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 pt-4">{t('outfitStack_empty_prompt')}</p>
        )}
        <AnimatePresence>
          {currentLayer.accessories.map(acc => renderItem(acc, true))}
        </AnimatePresence>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onGetStyleTips}
            disabled={!currentLayer.garments || currentLayer.garments.length === 0}
            title={t('nav_getStyleTip')}
            className={`flex-1 min-w-[120px] flex flex-col items-center justify-center text-center bg-gray-100 text-gray-800 font-semibold p-2 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-200 active:scale-95 text-xs dark:bg-gray-700/80 dark:text-gray-100 dark:hover:bg-gray-700 ${!currentLayer.mainGarment ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <MessageSquareIcon className="w-5 h-5 mb-1" />
            <span>{t('nav_getStyleTip')}</span>
          </button>
          <button
            onClick={onAnalyzeOutfit}
            disabled={!currentLayer.garments || currentLayer.garments.length === 0}
            title={t('myStylesPage_analyzeOutfit')}
            className={`flex-1 min-w-[120px] flex flex-col items-center justify-center text-center bg-gray-100 text-gray-800 font-semibold p-2 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-200 active:scale-95 text-xs dark:bg-gray-700/80 dark:text-gray-100 dark:hover:bg-gray-700 ${!currentLayer.mainGarment ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FileTextIcon className="w-5 h-5 mb-1" />
            <span>{t('myStylesPage_analyzeOutfit')}</span>
          </button>
          <button
            onClick={onSuggestVariations}
            disabled={!currentLayer.garments || currentLayer.garments.length === 0}
            title={t('outfitStack_suggestVariations')}
            className={`flex-1 min-w-[120px] flex flex-col items-center justify-center text-center bg-gray-100 text-gray-800 font-semibold p-2 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-200 active:scale-95 text-xs dark:bg-gray-700/80 dark:text-gray-100 dark:hover:bg-gray-700 ${!currentLayer.mainGarment ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <SparklesIcon className="w-5 h-5 mb-1" />
            <span>{t('outfitStack_suggestVariations')}</span>
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('outfitStack_creativeTools')}</h3>

          {/* Color Analysis Button */}
          <button
            onClick={onAnalyzeColor}
            disabled={!isModelLoaded || isLoading}
            title={t('outfitStack_analyzeColor')}
            className="w-full flex items-center justify-center text-center bg-purple-100 text-purple-700 border border-purple-200 font-semibold py-2 px-4 rounded-lg transition-colors duration-200 ease-in-out hover:bg-purple-200 active:scale-95 text-sm dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaletteIcon className="w-4 h-4 mr-2" />
            {t('outfitStack_analyzeColor')}
          </button>

          {/* AI Creative Tools Grid */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'ageing', icon: UserFocusIcon, label: t('creative_tool_ageing_label'), color: 'text-indigo-600 bg-indigo-50' },
              { id: 'wrinkles', icon: SparklesIcon, label: t('creative_tool_wrinkles_label'), color: 'text-emerald-600 bg-emerald-50' },
              { id: 'golden_hour', icon: SunIcon, label: t('creative_tool_golden_hour_label'), color: 'text-amber-600 bg-amber-50' },
              { id: 'remove_glasses', icon: GlassesIcon, label: t('creative_tool_remove_glasses_label'), color: 'text-blue-600 bg-blue-50' },
              { id: 'remove_hat', icon: HatIcon, label: t('creative_tool_remove_hat_label'), color: 'text-orange-600 bg-orange-50' },
              { id: 'remove_watch', icon: WatchIcon, label: t('creative_tool_remove_watch_label'), color: 'text-slate-600 bg-slate-50' },
              { id: 'remove_bag', icon: ShoppingBagIcon, label: t('creative_tool_remove_bag_label'), color: 'text-pink-600 bg-pink-50' },
            ].map((tool) => (
              <button
                key={tool.id}
                onClick={() => {
                  if (!hasPremiumAccess) {
                    setRequiredLevelForModal(t('profilePage_membership_premium'));
                    setIsPremiumModalOpen(true);
                  } else {
                    onApplyCreativeTool?.(tool.id as any);
                  }
                }}
                disabled={!isModelLoaded || isLoading}
                className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all group disabled:opacity-50"
                title={tool.label}
              >
                <div className={`p-2 rounded-full mb-1 group-hover:scale-110 transition-transform ${tool.color} dark:bg-opacity-10`}>
                  <tool.icon className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center line-clamp-1">{tool.label}</span>
              </button>
            ))}
          </div>

          {/* Attitude & Poise Section */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3 font-serif flex items-center">
              <SparklesIcon className="w-4 h-4 mr-2 text-amber-500" />
              {t('outfitStack_attitudePoise') || 'Attitude & Poise'}
            </h3>
            {currentLayer.attitudeNote ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/50 p-4 rounded-xl relative shadow-inner">
                <p className="text-sm text-gray-800 dark:text-gray-200 font-serif italic mb-3 leading-relaxed">
                  "{currentLayer.attitudeNote}"
                </p>
                {currentLayer.postureGuidance && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-sans border-t border-amber-200/30 dark:border-amber-800/30 pt-2 mt-2">
                    <span className="font-bold uppercase tracking-wider text-[10px] text-amber-600 dark:text-amber-500 mr-1">Posture:</span>
                    {currentLayer.postureGuidance}
                  </p>
                )}
              </motion.div>
            ) : (
              <button
                onClick={() => {
                  if (!hasPremiumAccess) {
                    setRequiredLevelForModal(t('profilePage_membership_premium'));
                    setIsPremiumModalOpen(true);
                  } else {
                    onGenerateAttitude?.();
                  }
                }}
                disabled={!isModelLoaded || isLoading || !currentLayer.garments || currentLayer.garments.length === 0}
                className="w-full flex items-center justify-center text-center bg-transparent text-gray-600 border border-dashed border-gray-300 font-semibold py-3 px-4 rounded-xl transition-all hover:bg-gray-50 hover:border-gray-400 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-800/50 mb-4 disabled:opacity-50 text-sm group"
              >
                <SparklesIcon className="w-4 h-4 mr-2 group-hover:text-amber-500 transition-colors" />
                {t('outfitStack_generateAttitude') || 'Generate Coaching Note'}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3 pt-4">
          <button
            id="action-shop-similar-stack"
            onClick={onFindSimilar}
            disabled={!isModelLoaded || !hasPremiumAccess}
            title={!isModelLoaded ? t('canvas_uploadToStart') : !hasPremiumAccess ? t('feature_locked_premium_generic') : t('outfitStack_findSimilar_main')}
            className="w-full flex items-center justify-center text-center bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-600 active:scale-95 text-base dark:bg-indigo-500 dark:hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SearchIcon className="w-5 h-5 mr-2" />
            {t('outfitStack_findSimilar_main')}
          </button>
          <button
            id="action-add-lookbook"
            onClick={onAddToLookbook}
            disabled={!currentLayer.garments || currentLayer.garments.length === 0 || !loadedOutfitId || isGuest}
            title={getLookbookTooltip()}
            className="w-full flex items-center justify-center text-center bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-600 active:scale-95 text-base dark:bg-indigo-500 dark:hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            {t('outfitStack_addToLookbook')}
          </button>
          <button
            id="action-save-stack"
            onClick={onSaveOutfit}
            disabled={!isModelLoaded || isLoading || isGuest}
            title={isGuest ? t('nav_guestCta') : ''}
            className="w-full flex items-center justify-center text-center bg-gray-900 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-base dark:bg-indigo-600 dark:hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SaveIcon className="w-5 h-5 mr-2" />
            {t('nav_save')}
          </button>
        </div>
      </div>
      <PremiumFeatureModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
        requiredLevel={requiredLevelForModal}
      />
    </div>
  );
};

export default OutfitStack;
